import { useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { BRAND } from '@/lib/brand';
import { generateSaasReportPdf, generateExecutiveSaasPdf } from '@/utils/pdfExecutive';

async function loadImageAsDataUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error('No se pudo cargar el logo');
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

const ACCENT_MAP = {
  á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u',
  Á: 'A', É: 'E', Í: 'I', Ó: 'O', Ú: 'U',
  ñ: 'n', Ñ: 'N', ü: 'u', Ü: 'U',
};

function sanitizeFilename(name) {
  const withoutAccents = String(name || 'grafico').replace(/[áéíóúÁÉÍÓÚñÑüÜ]/g, (c) => ACCENT_MAP[c] || c);
  const clean = withoutAccents.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return clean || 'grafico';
}

function timestamp() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}

/**
 * Captura el nodo referenciado por chartRef (html2canvas) y arma un PDF A4
 * con header (logo + empresa + fecha + usuario), el gráfico centrado y footer.
 * chartTitle se usa como título dentro del PDF y como base del nombre de archivo.
 */
export async function downloadChartAsPDF(chartRef, chartTitle, companyName, userName) {
  const node = chartRef?.current;
  if (!node) throw new Error('El gráfico no está montado todavía');

  const canvas = await html2canvas(node, {
    backgroundColor: '#1F2833',
    scale: 2,
    useCORS: true,
    logging: false,
  });
  // JPEG, no PNG: son capturas de pantalla completas (fondo solido, sin
  // transparencia -- backgroundColor fijo arriba) y el PDF combinado puede
  // llegar a pesar decenas de MB en PNG sin ganancia real de calidad.
  const imgData = canvas.toDataURL('image/jpeg', 0.85);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const now = new Date();
  const nowStr = now.toLocaleString('es-MX');

  try {
    const logoDataUrl = await loadImageAsDataUrl(BRAND.logo);
    doc.addImage(logoDataUrl, 'PNG', margin, margin - 8, 30, 10);
  } catch {
    // logo opcional: si falla la carga (offline, asset movido), el PDF se genera igual sin logo
  }

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(companyName || '—', pageWidth - margin, margin - 6, { align: 'right' });
  doc.text(nowStr, pageWidth - margin, margin - 2, { align: 'right' });
  doc.text(userName || '—', pageWidth - margin, margin + 2, { align: 'right' });

  doc.setDrawColor(210, 210, 210);
  doc.line(margin, margin + 4, pageWidth - margin, margin + 4);

  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(chartTitle || 'Gráfico', pageWidth / 2, margin + 12, { align: 'center' });

  const maxW = 180;
  const maxH = 140;
  const canvasAspect = canvas.width / canvas.height;
  const boxAspect = maxW / maxH;
  let imgW;
  let imgH;
  if (canvasAspect > boxAspect) {
    imgW = maxW;
    imgH = maxW / canvasAspect;
  } else {
    imgH = maxH;
    imgW = maxH * canvasAspect;
  }
  const imgX = (pageWidth - imgW) / 2;
  const imgY = margin + 18;
  doc.addImage(imgData, 'JPEG', imgX, imgY, imgW, imgH);

  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Generado desde Dashboard LeadCEM | ${nowStr}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' },
  );

  doc.save(`${sanitizeFilename(chartTitle)}_${timestamp()}.pdf`);
}

/**
 * Captura el nodo referenciado por tabRef (toda la pestaña: KPIs, gráficos y
 * tablas) y arma un PDF A4 multi-página. La imagen completa se re-dibuja en
 * cada página con un offset negativo (técnica estándar html2canvas+jsPDF);
 * cada página física recorta lo que no le corresponde, así que no hace falta
 * cortar el canvas a mano. Header con título "Reporte {tabName}" solo en la
 * página 1; footer "Página X de Y" en todas (se agrega al final, cuando ya
 * se conoce el total de páginas).
 */
export async function downloadTabAsPDF(tabRef, tabName, companyName, userName) {
  const node = tabRef?.current;
  if (!node) throw new Error('El contenido de la pestaña no está montado todavía');

  const canvas = await html2canvas(node, {
    backgroundColor: '#1F2833',
    scale: 2,
    useCORS: true,
    logging: false,
  });
  // JPEG, no PNG: son capturas de pantalla completas (fondo solido, sin
  // transparencia -- backgroundColor fijo arriba) y el PDF combinado puede
  // llegar a pesar decenas de MB en PNG sin ganancia real de calidad.
  const imgData = canvas.toDataURL('image/jpeg', 0.85);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
  const pageWidth = 210;
  const pageHeight = 297;
  const margin = 15;
  const footerZone = 10;
  const now = new Date();
  const nowStr = now.toLocaleString('es-MX');

  try {
    const logoDataUrl = await loadImageAsDataUrl(BRAND.logo);
    doc.addImage(logoDataUrl, 'PNG', margin, margin - 8, 30, 10);
  } catch {
    // logo opcional: si falla la carga, el PDF se genera igual sin logo
  }

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(companyName || '—', pageWidth - margin, margin - 6, { align: 'right' });
  doc.text(nowStr, pageWidth - margin, margin - 2, { align: 'right' });
  doc.text(userName || '—', pageWidth - margin, margin + 2, { align: 'right' });

  doc.setFontSize(15);
  doc.setTextColor(30, 30, 30);
  doc.text(`Reporte ${tabName || ''}`, margin, margin + 12);

  doc.setDrawColor(210, 210, 210);
  doc.line(margin, margin + 16, pageWidth - margin, margin + 16);

  const contentWidth = 180;
  const marginX = (pageWidth - contentWidth) / 2;
  const imgHeightTotal = (canvas.height * contentWidth) / canvas.width;

  const firstPageTop = margin + 22;
  const otherPageTop = margin;
  const firstPageUsable = pageHeight - firstPageTop - footerZone;
  const otherPageUsable = pageHeight - otherPageTop - footerZone;

  doc.addImage(imgData, 'JPEG', marginX, firstPageTop, contentWidth, imgHeightTotal);

  let heightLeft = imgHeightTotal - firstPageUsable;
  let renderedY = firstPageUsable;
  let pageNum = 1;

  while (heightLeft > 0) {
    doc.addPage();
    pageNum += 1;
    doc.addImage(imgData, 'JPEG', marginX, otherPageTop - renderedY, contentWidth, imgHeightTotal);
    heightLeft -= otherPageUsable;
    renderedY += otherPageUsable;
  }

  const totalPages = pageNum;
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Página ${p} de ${totalPages} | Generado desde Dashboard LeadCEM`, pageWidth / 2, pageHeight - 8, { align: 'center' });
  }

  doc.save(`Reporte_${sanitizeFilename(tabName)}_${timestamp()}.pdf`);
}

/**
 * Hook: expone downloadChartAsPDF/downloadTabAsPDF/exportDashboard con
 * estado `exporting` para deshabilitar el botón mientras html2canvas + jsPDF
 * generan el archivo.
 */
export default function useChartExport() {
  const [exporting, setExporting] = useState(false);

  const exportChart = useCallback(async (chartRef, chartTitle, companyName, userName) => {
    setExporting(true);
    try {
      await downloadChartAsPDF(chartRef, chartTitle, companyName, userName);
    } catch (e) {
      console.error('[useChartExport] fallo al generar PDF:', e);
    } finally {
      setExporting(false);
    }
  }, []);

  const exportTab = useCallback(async (tabRef, tabName, companyName, userName) => {
    setExporting(true);
    try {
      await downloadTabAsPDF(tabRef, tabName, companyName, userName);
    } catch (e) {
      console.error('[useChartExport] fallo al generar reporte de la pestaña:', e);
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * "Descargar Dashboard": reporte estilo SaaS (portada + índice + 1 página
   * FULLSCREEN por gráfico/tabla, raster). `switchTab` es un callback hacia
   * el caller (DashboardPage, dueño del estado de las Tabs) -- ver candado
   * de timing documentado en pdfExecutive.js/generateSaasReportPdf.
   * `visibleSections` ya debe venir filtrada por el caller (tabs_hidden +
   * charts_hidden), con `description` por chart (dashboardSections.js).
   */
  const exportDashboard = useCallback(async ({ company, userName, visibleSections, switchTab, waitMs, periodLabel }) => {
    setExporting(true);
    try {
      await generateSaasReportPdf({ company, userName, visibleSections, switchTab, waitMs, periodLabel });
    } catch (e) {
      console.error('[useChartExport] fallo al generar el dashboard combinado:', e);
    } finally {
      setExporting(false);
    }
  }, []);

  /**
   * "Descargar Reporte Ejecutivo": misma arquitectura SaaS que
   * exportDashboard (portada + 1 página fullscreen por gráfico +
   * header/footer), pero con gráficos FIJOS -- `fixedCharts` los resuelve
   * el caller vía getExecutiveFixedChart() (dashboardSections.js), uno por
   * tab disponible por LICENCIA, ignorando dashboard_prefs a propósito.
   * Página 2 es un resumen de KPIs reales, no un índice.
   */
  const exportExecutive = useCallback(async ({ company, userName, fixedCharts, kpis, switchTab, waitMs, periodLabel }) => {
    setExporting(true);
    try {
      await generateExecutiveSaasPdf({ company, userName, fixedCharts, kpis, switchTab, waitMs, periodLabel });
    } catch (e) {
      console.error('[useChartExport] fallo al generar el reporte ejecutivo:', e);
    } finally {
      setExporting(false);
    }
  }, []);

  return { exportChart, exportTab, exportDashboard, exportExecutive, exporting };
}
